import { jwtDecode } from 'jwt-decode';
import { useEffect, useMemo, useState } from 'react';
import createContainer from 'constate';
import config from '../../config';
import { HUBBLE_SESSION_AUTH, LOGIN_STATES } from '../Login';
import Session from '../Session';

/**
 * Hubble Permissions Module
 *
 * @description A simple permissions module that uses Microsoft Entra Id groups and users to enforce
 * granular permissions and feature flags across the app. Leverages Microsoft OAuth JWT tokens
 * to compare against the token's groups list and oid (user id within Entra).
 *
 * @overview
 * - Uses Microsoft OAuth authentication JWT tokens for permission checking
 * - Supports both group-based and user-based permissions
 * - Permissions and feature flags serve the same purpose - boolean values for conditional rendering
 * - Automatically handles initial auth and re-auth flows via custom login events
 *
 * @usage
 * ```js
 * import { usePermissions } from "@modules/Permissions";
 *
 * export const MyComponent = () => {
 *   const { isInCoolUserClub, isInCoolGroupClub } = usePermissions();
 *   return (
 *     <>
 *       <div>This is a public div, please enjoy.</div>
 *       { isInCoolUserClub && <div>Really cool users only.</div> }
 *       { isInCoolGroupClub && <div>Really cool group members only.</div> }
 *     </>
 *   );
 * };
 * ```
 *
 * @howToAddNewPermissions
 * 1. Add a new descriptive permissions key in the app config file (config.js):
 *    ```js
 *    permissions: {
 *      coolGroupId: 'guid here',
 *      coolUserId: 'guid here',
 *    }
 *    ```
 *
 * 2. Add a new permissions entry in this module's useMemo return object:
 *    ```js
 *    const permissions = useMemo(() => {
 *      if (!jwt) return {};
 *
 *      const { groups, oid } = jwtDecode(jwt);
 *      const compareOid = (query) => oid === query;
 *      const compareGroupId = (query) => groups.includes(query);
 *
 *      return {
 *        isInCoolUserClub: compareOid(config.permissions.coolUserId),
 *        isInCoolGroupClub: compareGroupId(config.permissions.coolGroupId),
 *      };
 *    }, [jwt]);
 *    ```
 *
 * 3. Import and use in your component as shown in the usage example above
 *
 * @returns {Object} Object containing boolean permission flags
 *
 * @see {@link https://learn.microsoft.com/en-us/entra/identity-platform/access-token-claims-reference} Microsoft OAuth token claims reference
 */
const PermissionsModule = () => {
  const { token: initToken } = Session.get(HUBBLE_SESSION_AUTH);
  const [jwt, setJwt] = useState(initToken);

  const permissions = useMemo(() => {
    if (!jwt) return {};

    const { groups, oid } = jwtDecode(jwt);
    const compareOid = (query) => oid === query;
    const compareGroupId = (query) => groups.includes(query);

    return {
      hasAndromedaComplianceAccess: compareGroupId(
        config.permissions.andromedaComplianceAdminGroupId
      ),
      hasCodeExchangeAccess: compareGroupId(
        config.permissions.codeExchangeGroupId
      ),
      hasAndromedaAccess: compareGroupId(
        config.permissions.andromedaAccessGroupId
      ),
      hasBatchYodleeRelinkAccess: compareGroupId(
        config.permissions.batchYodleeRelinkGroupId
      ),
      hasIntegratedTransitionAccess: compareGroupId(
        config.permissions.integratedTransitionGroupId
      ),
      hasFundReconciliationAccess: compareGroupId(
        config.permissions.fundReconciliationGroupId
      ),
    };
  }, [jwt]);

  /*
   * This useEffect handles initial auth and re-auth flows here
   * The Oauth login component dispatches a loggedin custom event when successful
   * Without this, tokens are not properly loaded from sessionStorage during re-auth flows and auth healthchecks
   *
   */
  useEffect(() => {
    const handlelogin = (e) => {
      setJwt(e.detail.token);
    };

    window.addEventListener(LOGIN_STATES.loggedin, handlelogin);

    return () => window.removeEventListener(LOGIN_STATES.loggedin, handlelogin);
  }, []);

  /*
   * To add a new permission
   *   1. Add a new Microsoft Entra Id group id to the permissions object in the config file. Please name it descriptively
   *   2. Add a new permission of with the same name to the permissions object return from the useMemo() function at the top of this module
   *
   */
  return permissions;
};

const [Provider, usePermissions] = createContainer(PermissionsModule);

export { usePermissions };

export default Provider;
